import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from '../primitives';
import { Select } from '../select';

interface IBirthdateInput {
  defaultValue?: Date;
  onChange: (date: Date) => void;
}

const DATE_FORMAT = 'YYYY-MM-DD';
const LEGAL_AGE = 18;
const NB_YEARS = 100;

export const BirthdateInput = ({
  defaultValue = dayjs('1970-01-01').toDate(),
  onChange,
}: IBirthdateInput) => {
  const { t } = useTranslation();
  const now = new Date();

  const currentDayInMonth = now.getDate();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  const birthDayYearLimit = currentYear - LEGAL_AGE;
  const parsedDefaultValue = dayjs(defaultValue, DATE_FORMAT);

  const [day, setDay] = useState(parsedDefaultValue.date());
  const [month, setMonth] = useState(parsedDefaultValue.month());
  const [year, setYear] = useState(parsedDefaultValue.year());

  const getDisabledListLimit = (arrayLength: number, shift = 0) =>
    [...new Array(arrayLength)].map(
      (_: number, index: number) => shift + index + 1
    );

  const [disabledDays, setDisabledDays] = useState<number[]>(
    birthDayYearLimit === year
      ? getDisabledListLimit(31 - currentDayInMonth, currentDayInMonth)
      : currentYear - NB_YEARS === year
      ? getDisabledListLimit(currentDayInMonth)
      : []
  );

  const [disabledMonths, setDisabledMonths] = useState<number[]>(
    birthDayYearLimit === year
      ? getDisabledListLimit(12 - (currentMonth + 1), currentMonth)
      : currentYear - NB_YEARS === year
      ? getDisabledListLimit(currentMonth, -1)
      : []
  );

  const daysList = useMemo(
    () =>
      [...new Array(31)].map((_, index) => ({
        id: index + 1,
        label: index + 1 < 10 ? `0${index + 1}` : String(index + 1),
        disabledOption: disabledDays.includes(index + 1),
      })),
    [disabledDays]
  );

  const monthsList = useMemo(
    () =>
      (t('birthdateInput.months', {
        returnObjects: true,
      }) as string[]).map((month: string, index: number) => ({
        id: index,
        label: month,
        disabledOption: disabledMonths.includes(index),
      })),
    [disabledMonths]
  );

  const yearsList = useMemo(
    () =>
      [...new Array(NB_YEARS + 1 - LEGAL_AGE)].map((_, index) => {
        const filteredYear = birthDayYearLimit - index;

        return {
          id: filteredYear,
          label: String(filteredYear),
        };
      }),
    [birthDayYearLimit]
  );

  const handleChangeMonth = (newMonth: number) => {
    switch (true) {
      case newMonth === currentMonth && year === birthDayYearLimit:
        setDay(day > currentDayInMonth ? currentDayInMonth : day);

        setDisabledDays(
          getDisabledListLimit(31 - currentDayInMonth, currentDayInMonth)
        );

        onChange(new Date(year, newMonth, currentDayInMonth));
        break;
      case newMonth === currentMonth && year === currentYear - NB_YEARS:
        setDay(day <= currentDayInMonth ? currentDayInMonth + 1 : day);

        setDisabledDays(getDisabledListLimit(currentDayInMonth));

        onChange(new Date(year, newMonth, currentDayInMonth + 1));
        break;
      default:
        setDisabledDays([]);

        onChange(new Date(year, newMonth, day));
    }
  };

  const handleChangeYear = (newYear: number) => {
    switch (true) {
      case newYear === birthDayYearLimit:
        setMonth(month > currentMonth ? currentMonth : month);

        setDisabledMonths(
          getDisabledListLimit(12 - (currentMonth + 1), currentMonth)
        );

        if (month >= currentMonth) {
          setDay(day > currentDayInMonth ? currentDayInMonth : day);

          setDisabledDays(
            getDisabledListLimit(31 - currentDayInMonth, currentDayInMonth)
          );
        }

        onChange(new Date(newYear, currentMonth, currentDayInMonth));
        break;
      case newYear === currentYear - NB_YEARS:
        setMonth(month < currentMonth ? currentMonth : month);

        setDisabledMonths(getDisabledListLimit(currentMonth, -1));

        if (month <= currentMonth) {
          setDay(day <= currentDayInMonth ? currentDayInMonth + 1 : day);
          setDisabledDays(getDisabledListLimit(currentDayInMonth));
        }

        onChange(new Date(newYear, currentMonth, currentDayInMonth + 1));
        break;
      default:
        setDisabledDays([]);
        setDisabledMonths([]);

        onChange(new Date(newYear, month, day));
    }
  };

  return (
    <Flex>
      <Select
        label="daysList"
        values={daysList}
        value={day}
        mr="medium"
        onChange={(newDay: number) => {
          setDay(newDay);
          onChange(new Date(year, month, newDay));
        }}
      />
      <Select
        label="monthsList"
        values={monthsList}
        value={month}
        mr="medium"
        onChange={(newMonth: number) => {
          setMonth(newMonth);
          handleChangeMonth(newMonth);
        }}
      />
      <Select
        label="yearList"
        values={yearsList}
        value={year}
        onChange={(newYear: number) => {
          setYear(newYear);
          handleChangeYear(newYear);
        }}
      />
    </Flex>
  );
};
